import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ToolsPanel } from '../components/ui/tools-panel';
import { EcosystemNav } from '../components/ui/ecosystem-nav';
import { useAuth, SignInBanner } from '../components/ui/auth-components';
import {
  ArrowLeft, Users, FileText, ChevronRight, ChevronLeft, Check, Circle,
  Calculator, RefreshCw, Save, Menu, X, Share2, Shield, Plus,
  PanelRightClose, PanelRightOpen, Download, Home, LogIn, Copy,
  BookOpen, ExternalLink, ArrowUpRight, ListTodo
} from 'lucide-react';
import { toast } from 'sonner';
import {
  GoldenSpiralIcon,
  SacredFounderIcon,
  LotusIcon,
  AncientSunIcon,
  CompassIcon as LegendaryCompass,
  MandalaIcon,
  MetatronIcon,
  BrandCheckerIcon,
  EmergencySOSIcon,
  SacredListIcon,
  SacredFocusIcon,
  SacredCheckIcon
} from '../components/ui/legendary-icons';
import { BrandChecker } from '../components/ui/brand-checker';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ELCHME_URL = 'https://elchme.com';

// ============ STARTUP THEME ============
const STARTUP_THEME = {
  primary: '#4F46E5',
  secondary: '#6366F1',
  accent: '#F59E0B',
  background: 'rgba(15, 23, 42, 0.95)',
};

// ============ MYSTICAL ICONS FOR COMPASS ============
const MysticalIcons = {
  center: (props) => (
    <svg viewBox="0 0 32 32" fill="none" {...props}>
      <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="1" opacity="0.3"/>
      <circle cx="16" cy="16" r="8" stroke="currentColor" strokeWidth="1.5" opacity="0.6"/>
      <circle cx="16" cy="16" r="2" fill="currentColor"/>
    </svg>
  ),
  north: (props) => (
    <svg viewBox="0 0 32 32" fill="none" {...props}>
      <path d="M16 4L12 16L16 28L20 16L16 4Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.2"/>
      <circle cx="16" cy="16" r="3" fill="currentColor"/>
    </svg>
  ),
  northeast: (props) => (
    <svg viewBox="0 0 32 32" fill="none" {...props}>
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="20" cy="12" r="4" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="16" cy="20" r="4" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M14 14L16 18L18 14" stroke="currentColor" strokeWidth="1"/>
    </svg>
  ),
  east: (props) => (
    <svg viewBox="0 0 32 32" fill="none" {...props}>
      <rect x="6" y="8" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/>
      <line x1="6" y1="14" x2="26" y2="14" stroke="currentColor" strokeWidth="1"/>
      <line x1="16" y1="14" x2="16" y2="24" stroke="currentColor" strokeWidth="1"/>
    </svg>
  ),
  southeast: (props) => (
    <svg viewBox="0 0 32 32" fill="none" {...props}>
      <circle cx="16" cy="16" r="10" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M16 6V16L23 23" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="16" cy="16" r="2" fill="currentColor"/>
    </svg>
  ),
  south: (props) => (
    <svg viewBox="0 0 32 32" fill="none" {...props}>
      <path d="M8 12L16 4L24 12L20 12L20 24L12 24L12 12L8 12Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.2"/>
    </svg>
  ),
  southwest: (props) => (
    <svg viewBox="0 0 32 32" fill="none" {...props}>
      <path d="M6 20C6 20 10 8 16 8C22 8 26 20 26 20" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="16" cy="20" r="4" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M12 28L16 24L20 28" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  west: (props) => (
    <svg viewBox="0 0 32 32" fill="none" {...props}>
      <rect x="8" y="6" width="16" height="20" rx="1" stroke="currentColor" strokeWidth="1.5"/>
      <line x1="12" y1="10" x2="20" y2="10" stroke="currentColor" strokeWidth="1"/>
      <line x1="12" y1="14" x2="18" y2="14" stroke="currentColor" strokeWidth="1"/>
      <line x1="12" y1="18" x2="16" y2="18" stroke="currentColor" strokeWidth="1"/>
      <rect x="12" y="21" width="8" height="2" fill="currentColor" opacity="0.5"/>
    </svg>
  ),
  northwest: (props) => (
    <svg viewBox="0 0 32 32" fill="none" {...props}>
      <circle cx="16" cy="16" r="10" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M10 16H22M16 10V22" stroke="currentColor" strokeWidth="1"/>
      <circle cx="16" cy="16" r="4" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  )
};

// ============ UNIFIED 8-POINT COMPASS (Plain English) ============
// Enhanced subcategories blending business clarity with community wisdom
// Includes CPG New Product Development best practices (Stage-Gate, Cooper's methodology)
const STARTUP_SECTIONS = [
  {
    key: 'north',
    compassPoint: 'N',
    title: 'Vision & Purpose',
    subtitle: 'Your North Star',
    icon: MysticalIcons.north,
    color: '#D97706',
    description: 'Why you exist and where you\'re going',
    furtherReading: [
      { title: 'Built to Last (Collins & Porras)', note: 'Core values and purpose that endure' },
      { title: 'Start With Why (Simon Sinek)', note: 'Finding your "why"' },
      { title: 'Beyond Entrepreneurship 2.0 (Collins)', note: 'Vision framework and BHAGs' },
      { title: 'Winning at New Products (Robert Cooper)', note: 'Stage-Gate NPD process' }
    ],
    addressedIn: ['Community Charter: Soul & Signal'],
    subElements: [
      { key: 'origin_story', title: 'Origin Story', prompt: 'What life experience sparked this? What wound are you healing? Why are YOU the one to do it?', guidance: 'People invest in people. Share the real story—the pain, the insight, the calling.' },
      { key: 'core_values', title: 'Core Values', prompt: 'What 3-5 beliefs will NEVER change, even if they cost you money or members?', guidance: 'Values aren\'t made up—they\'re discovered from your actions. What hills will you die on?' },
      { key: 'purpose_statement', title: 'Purpose Statement', prompt: 'Complete this: "We exist to..." (not to make money—that\'s a result)', guidance: 'This should make you want to get out of bed each morning. It\'s your "Why" distilled.' },
      { key: 'bhag', title: 'Big Hairy Audacious Goal', prompt: 'What\'s your bold 10-25 year goal that seems almost impossible right now?', guidance: 'Think "put a person on the moon" level of ambition. Make it vivid and measurable.' },
      { key: 'vivid_description', title: 'Picture of Success', prompt: 'Describe what the world looks like when you succeed. Paint the picture!', guidance: 'Help people see and feel what you\'re building toward. Make it real enough to taste.' },
      { key: 'theory_of_change', title: 'Theory of Change', prompt: 'How exactly does your work create the change you seek? What\'s the chain of cause and effect?', guidance: 'If we do X, then Y will happen, because Z. Be specific about the mechanism.' },
      { key: 'product_vision', title: '🆕 Product Vision Statement', prompt: 'What new product or innovation are you bringing to market? What unmet need does it address? What makes it better than existing solutions?', guidance: 'For CPG/NPD: Define the product concept clearly. What category is it? What consumer job does it do? What\'s the unique benefit or technology? Think: "A [product type] that [key benefit] for [target consumer] because [reason to believe]."' }
    ]
  },
  {
    key: 'northeast',
    compassPoint: 'NE',
    title: 'Team & Trust',
    subtitle: 'Your People',
    icon: MysticalIcons.northeast,
    color: '#2f6f3e',
    description: 'Who you\'re building with and how you work together',
    furtherReading: [
      { title: 'Good to Great (Jim Collins)', note: 'First who, then what' },
      { title: 'The Five Dysfunctions of a Team (Lencioni)', note: 'Building trust' },
      { title: 'Beyond Entrepreneurship 2.0', note: 'Level 5 leadership' }
    ],
    addressedIn: ['Community Charter: Team & Trust'],
    subElements: [
      { key: 'founding_team', title: 'Founding Team', prompt: 'Who\'s on your core team? How did you meet? What binds you together beyond the work?', guidance: 'Strong teams have shared history and complementary skills. Chemistry matters.' },
      { key: 'fire_roles', title: 'Fire Roles', prompt: 'Who are your Fire Keepers (steady in crisis), Fire Sitters (growing), and who needs stabilizing?', guidance: 'A Fire Keeper is revealed in crisis, not in calm. Know who holds steady when things burn.' },
      { key: 'stewardship_core', title: 'Stewardship Core', prompt: 'Who are the 3-5 people carrying the weight? How do you prevent founder burnout?', guidance: 'Without a Stewardship Core, the founder becomes the default—and that always ends in burnout.' },
      { key: 'key_seats', title: 'Key Seats', prompt: 'What 5-10 roles are most critical? Who fills them now? What seats are empty?', guidance: 'Name the seats on the bus before filling them. Right people in right seats.' },
      { key: 'trust_building', title: 'Trust Practices', prompt: 'How do you build and maintain trust? What happens when trust breaks?', guidance: 'Trust = displayed credibility earned through participation in repair. How do you repair?' },
      { key: 'working_agreements', title: 'Working Agreements', prompt: 'What explicit agreements do you have about how you work together?', guidance: 'Clarity is kindness. Unspoken expectations become resentments.' },
      { key: 'leadership_philosophy', title: 'Leadership Philosophy', prompt: 'How do you lead? What\'s your theory of leadership?', guidance: 'Great leaders are humble about themselves but fierce about the mission.' },
      { key: 'team_belonging', title: 'System of Belonging', prompt: 'How do team members belong to EACH OTHER—not just the mission? What rituals create genuine connection?', guidance: 'Building a system of belonging is equally (if not MORE) important than the business model, product, or profitability. When you create something challenging—or equally when you create something successful—if you\'re not built on a foundation of belonging, the probability of failure is excessively high. Teams that don\'t belong to each other fracture under pressure AND under success. Explore: elchme.com, belongingsessions.com' },
      { key: 'npd_cross_functional', title: '🆕 Cross-Functional NPD Team', prompt: 'For new product development: Who leads R&D/formulation? Marketing? Operations/Supply Chain? Quality? Sales? How do these functions collaborate through the Stage-Gate process?', guidance: 'Best-in-class CPG NPD requires tight cross-functional alignment. Define: Executive Sponsor, NPD Lead/Project Manager, R&D/Formulation, Marketing/Consumer Insights, Operations/Manufacturing, Quality/Regulatory, Sales/Trade Marketing, Finance. How often do they meet? How are go/no-go decisions made?' }
    ]
  },
  {
    key: 'east',
    compassPoint: 'E',
    title: 'Story & Identity',
    subtitle: 'How You Show Up',
    icon: MysticalIcons.east,
    color: '#6366F1',
    description: 'Your brand, message, and how people experience you',
    furtherReading: [
      { title: 'Building a StoryBrand (Donald Miller)', note: 'Clarifying your message' },
      { title: 'Made to Stick (Heath Brothers)', note: 'Ideas that last' },
      { title: 'Positioning (Ries & Trout)', note: 'Owning a space in the mind' }
    ],
    addressedIn: ['Community Charter: Story & Design'],
    subElements: [
      { key: 'hero_customer', title: 'The Hero\'s Journey', prompt: 'Who is your ideal customer/member? What transformation do they seek?', guidance: 'They are the hero. You are the guide. What\'s their journey from struggle to success?' },
      { key: 'problem_statement', title: 'The Problem', prompt: 'What problem keeps your hero up at night? Make them feel understood.', guidance: '"Everyone" is not a customer. Get specific about one person\'s pain.' },
      { key: 'solution_promise', title: 'Your Promise', prompt: 'What solution do you offer? What transformation do you enable?', guidance: 'Value = what they get minus what they give up. Make the promise clear.' },
      { key: 'positioning_statement', title: 'Positioning Statement (25 Words)', prompt: 'In exactly 25 words or less, write the pitch that INSTANTLY sells the whole thing. This is your positioning statement.', guidance: 'This is the sentence that makes people lean in. Every word must earn its place. Test it: can a stranger repeat it after hearing it once?' },
      { key: 'brand_name', title: 'Brand Name', prompt: 'What is your brand name? Why did you choose it? What does it evoke?', guidance: 'Great names are memorable, easy to spell, available as a domain, and don\'t have negative associations. Use the Brand Name Checker tool to validate!' },
      { key: 'brand_voice', title: 'Brand Voice', prompt: 'How do you sound? Serious? Friendly? Expert? Rebellious? Nurturing?', guidance: 'Consistent voice builds recognition and trust. Be authentic.' },
      { key: 'origin_narrative', title: 'Origin Narrative', prompt: 'What\'s the founding story you tell? The myth that defines you?', guidance: 'Every great organization has a creation story. What\'s yours?' },
      { key: 'key_message', title: 'Elevator Pitch', prompt: 'If someone had 30 seconds, what would you tell them?', guidance: '"For [who] who [problem], we [solution] unlike [alternatives]."' },
      { key: 'visual_identity', title: 'Visual Identity & Logo', prompt: 'What do you look like? Colors, symbols, logo concept, design language?', guidance: 'Visual consistency signals professionalism and creates recognition. What emotions should your aesthetic evoke?' },
      { key: 'ip_due_diligence', title: 'IP & Regulatory Due Diligence', prompt: 'Have you checked for trademark conflicts, patent issues, and regulatory requirements? What liabilities might you face?', guidance: 'Before investing deeply: 1) Search USPTO for trademark conflicts, 2) Check if domain is available (and what it currently shows!), 3) Ensure domain doesn\'t point to anything nefarious or misaligned, 4) Research regulatory requirements in your industry, 5) Identify potential patent conflicts. This protects your future.' },
      { key: 'packaging_design', title: '🆕 Packaging & Shelf Presence', prompt: 'For CPG: What is your packaging strategy? Primary and secondary packaging? Shelf presence and planogram strategy? Sustainability considerations?', guidance: 'Packaging is your silent salesperson. Consider: Structural design, graphic design, material selection, sustainability (recyclable, compostable?), cost implications, shelf visibility at 5 feet, legal/regulatory requirements (nutrition facts, claims). What story does the package tell in 3 seconds?' }
    ]
  },
  {
    key: 'southeast',
    compassPoint: 'SE',
    title: 'Market & Landscape',
    subtitle: 'Your Playing Field',
    icon: MysticalIcons.southeast,
    color: '#059669',
    description: 'Understanding the ecosystem and your position in it',
    furtherReading: [
      { title: 'Blue Ocean Strategy (Kim & Mauborgne)', note: 'Creating new markets' },
      { title: 'Crossing the Chasm (Geoffrey Moore)', note: 'Technology adoption' },
      { title: 'Beyond Entrepreneurship 2.0', note: 'Market timing' },
      { title: 'IRI/Nielsen Category Data', note: 'CPG category analysis' }
    ],
    addressedIn: ['Community Charter: Infrastructure & Context'],
    subElements: [
      { key: 'market_size', title: 'Market Size', prompt: 'How big is your opportunity? Show the math: TAM → SAM → SOM.', guidance: 'Total Addressable Market → Serviceable Addressable → Serviceable Obtainable.' },
      { key: 'why_now', title: 'Why Now?', prompt: 'What changes make this the right time? Technology? Behavior? Regulation? Culture?', guidance: 'Timing matters as much as the idea. What wave are you riding?' },
      { key: 'ecosystem_map', title: 'Ecosystem Map', prompt: 'Who are the key players? Partners? Competitors? Substitutes? Influencers?', guidance: 'Draw the landscape. Who has power? Where does value flow?' },
      { key: 'competitors', title: 'Competition Analysis', prompt: 'Who else solves this problem? Direct competitors? Alternatives? Status quo?', guidance: '"No competition" = no market or you haven\'t looked hard enough.' },
      { key: 'competitive_moat', title: 'Your Moat', prompt: 'What makes your advantage hard to copy? What\'s your defensible position?', guidance: 'Network effects, brand, switching costs, secrets, speed, relationships.' },
      { key: 'positioning', title: 'Positioning', prompt: 'How are you different from alternatives? What\'s your unique angle?', guidance: 'You can\'t be everything to everyone. Choose your lane clearly.' },
      { key: 'consumer_insights', title: '🆕 Consumer Insights Research', prompt: 'What deep consumer research have you conducted? Focus groups? Surveys? Ethnography? Jobs-to-be-Done interviews? What surprised you about consumer behavior?', guidance: 'CPG best practice: Understand not just WHAT consumers buy but WHY. Conduct JTBD interviews ("When did you last buy X? Walk me through that moment."), home visits, shop-alongs. Look for unmet needs, occasions, pain points. Quantify the opportunity.' },
      { key: 'category_analysis', title: '🆕 Category & Channel Analysis', prompt: 'What category do you compete in? What are the category trends? Which retail channels matter most (grocery, mass, club, e-commerce, natural)?', guidance: 'Understand category dynamics: Is it growing or declining? What price tiers exist? What\'s the channel mix (Walmart, Kroger, Target, Amazon, Whole Foods)? What are retailer priorities? Who are the category captains?' }
    ]
  },
  {
    key: 'south',
    compassPoint: 'S',
    title: 'Proof & Learning',
    subtitle: 'Testing Reality',
    icon: MysticalIcons.south,
    color: '#DC2626',
    description: 'Validating assumptions and gathering evidence',
    furtherReading: [
      { title: 'The Lean Startup (Eric Ries)', note: 'Build-measure-learn' },
      { title: 'The Mom Test (Rob Fitzpatrick)', note: 'Customer interviews' },
      { title: 'Beyond Entrepreneurship 2.0', note: 'Bullets before cannonballs' },
      { title: 'Winning at New Products (Cooper)', note: 'Stage-Gate validation' }
    ],
    addressedIn: ['Community Charter: Activation & Experiments'],
    subElements: [
      { key: 'assumptions_list', title: 'Key Assumptions', prompt: 'What must be true for this to work? List your riskiest assumptions.', guidance: 'Be honest about what you\'re betting on. Which assumptions could kill you?' },
      { key: 'customer_discovery', title: 'Customer Discovery', prompt: 'How many people have you talked to? What surprised you? What did you learn?', guidance: 'Talk to 50+ people. Listen more than pitch. Ask about their life, not your idea.' },
      { key: 'problem_validation', title: 'Problem Validation', prompt: 'What evidence shows the problem is real and worth solving?', guidance: 'Actions > words. What do people actually do (vs. say they\'d do)?' },
      { key: 'solution_experiments', title: 'Solution Experiments', prompt: 'How have you tested your solution? What worked? What flopped?', guidance: 'Build small, learn fast, adjust often. Bullets before cannonballs.' },
      { key: 'traction_evidence', title: 'Traction Evidence', prompt: 'What results do you have? Users? Revenue? Growth? Engagement?', guidance: 'Traction speaks louder than promises. Show don\'t tell.' },
      { key: 'pivots_learnings', title: 'Pivots & Learnings', prompt: 'What changed your mind? What pivots have you made? What would you do differently?', guidance: 'Smart pivots based on evidence show maturity, not failure.' },
      { key: 'concept_testing', title: '🆕 Concept Testing & Validation', prompt: 'Have you tested the product concept? What methodology (CLT, IHUT, monadic, sequential)? What were purchase intent scores? How did it perform vs. benchmarks?', guidance: 'CPG Stage-Gate best practice: Test concepts before development. Key metrics: Purchase Intent (Top 2 Box), Uniqueness, Relevance, Value. Compare to category norms. Use qual + quant. Iterate based on learnings.' },
      { key: 'prototype_testing', title: '🆕 Product/Prototype Testing', prompt: 'Have you developed prototypes? Conducted taste tests or use tests? How does product perform vs. competitive set? What\'s the sensory profile?', guidance: 'Product must deliver on the promise. Test blind vs. branded. Measure: Overall liking, attribute diagnostics, purchase intent after trial. Is there a "wow" moment? What\'s the repeat purchase likelihood?' }
    ]
  },
  {
    key: 'southwest',
    compassPoint: 'SW',
    title: 'Growth & Connection',
    subtitle: 'Reaching People',
    icon: MysticalIcons.southwest,
    color: '#8B5CF6',
    description: 'How you find, win, and nurture relationships',
    furtherReading: [
      { title: 'Traction (Weinberg & Mares)', note: '19 channels for growth' },
      { title: 'Hooked (Nir Eyal)', note: 'Building habits' },
      { title: 'Beyond Entrepreneurship 2.0', note: 'Disciplined growth' },
      { title: 'Trade Marketing Excellence', note: 'CPG retail execution' }
    ],
    addressedIn: ['Community Charter: Connection & Exchange'],
    subElements: [
      { key: 'ideal_member', title: 'Ideal Member/Customer', prompt: 'Describe your perfect person in vivid detail. What do they look like? Feel like?', guidance: 'Tight focus enables efficient effort. One specific person, not demographics.' },
      { key: 'acquisition_channels', title: 'Acquisition Channels', prompt: 'What are your top 3 ways to reach new people? What\'s working?', guidance: 'Focus on 1-2 channels until they work, then expand. Don\'t spread thin.' },
      { key: 'onboarding_journey', title: 'Onboarding Journey', prompt: 'What happens in someone\'s first hour? First day? First week?', guidance: 'The first experience shapes everything. Design it intentionally.' },
      { key: 'pricing_value', title: 'Pricing & Value', prompt: 'How do you price? What\'s the value exchange? What\'s the logic?', guidance: 'Value-based pricing beats cost-plus. What\'s it worth to them?' },
      { key: 'sales_conversion', title: 'Sales/Conversion Process', prompt: 'Walk through your process from first touch to commitment.', guidance: 'Make it repeatable. Document what works. Remove friction.' },
      { key: 'retention_engagement', title: 'Retention & Engagement', prompt: 'How do you keep people? What creates stickiness and deepening engagement?', guidance: 'Keeping people is cheaper than finding new ones. What creates belonging?' },
      { key: 'referral_growth', title: 'Referral & Word-of-Mouth', prompt: 'How do happy members bring others? What makes people want to share?', guidance: 'The best growth is organic. What makes this remarkable enough to mention?' },
      { key: 'go_to_market', title: '🆕 Go-to-Market Strategy', prompt: 'For CPG: What\'s your retail strategy? DTC first or retail first? Which retailers and why? What\'s your broker/distributor strategy? Regional rollout vs. national launch?', guidance: 'CPG GTM options: 1) DTC to prove demand, then retail. 2) Natural channel first (Whole Foods, Sprouts), then expand. 3) Regional grocery chains, then national. Consider: Retailer requirements, slotting fees, broker relationships, distribution partners (UNFI, KeHE, DSD). Have a clear path.' },
      { key: 'trade_marketing', title: '🆕 Trade & Shopper Marketing', prompt: 'What\'s your trade marketing plan? Promotional strategy? In-store execution? Shopper marketing tactics? How will you get trial at shelf?', guidance: 'You need both pull (consumer marketing) and push (trade marketing). Plan for: Slotting/placement fees, promotional calendar (TPRs, BOGO), demo programs, display/shipper strategy, co-op marketing with retailers. Trial is everything for CPG launch.' }
    ]
  },
  {
    key: 'west',
    compassPoint: 'W',
    title: 'Execution & Rhythm',
    subtitle: 'Getting It Done',
    icon: MysticalIcons.west,
    color: '#F59E0B',
    description: 'Turning plans into action with discipline',
    furtherReading: [
      { title: 'Great by Choice (Jim Collins)', note: 'The 20-Mile March' },
      { title: 'Measure What Matters (John Doerr)', note: 'OKRs' },
      { title: 'Beyond Entrepreneurship 2.0', note: 'Disciplined execution' }
    ],
    addressedIn: ['Community Charter: Rhythm & Ritual'],
    subElements: [
      { key: 'north_star_metric', title: 'North Star Metric', prompt: 'What one number best shows if you\'re winning? The metric that matters most.', guidance: 'If you could only track one thing, what would tell you if you\'re on track?' },
      { key: 'quarterly_objectives', title: 'Quarterly Objectives', prompt: 'What are your top 3 goals for this quarter? What defines success?', guidance: 'Objectives = what you want. Key Results = how you know you did it.' },
      { key: 'weekly_rhythm', title: 'Weekly Rhythm', prompt: 'What\'s your consistent weekly cadence? Meetings? Check-ins? Reviews?', guidance: 'Rhythm creates reliability. What happens every week no matter what?' },
      { key: 'key_milestones', title: 'Key Milestones', prompt: 'What major milestones are you working toward? When?', guidance: 'Specific. Measurable. Time-bound. Celebrated when achieved.' },
      { key: 'twenty_mile_march', title: '20-Mile March', prompt: 'What consistent progress will you make every week/month regardless of conditions?', guidance: 'Steady progress beats heroic sprints. What\'s your non-negotiable pace?' },
      { key: 'risks_mitigations', title: 'Risks & Mitigations', prompt: 'What could go wrong? What\'s your backup plan? Your "pre-mortem"?', guidance: 'Hope for the best, plan for the worst. What kills companies like yours?' },
      { key: 'decision_framework', title: 'Decision Framework', prompt: 'How do you make decisions? Who decides what? How do you resolve conflicts?', guidance: 'Clear decision rights prevent paralysis. Who has what authority?' },
      { key: 'stage_gate_process', title: '🆕 Stage-Gate NPD Process', prompt: 'What is your formal new product development process? Define your stages (Discovery, Scoping, Business Case, Development, Testing, Launch) and gates (Go/Kill decisions). Who makes gate decisions?', guidance: 'Cooper\'s Stage-Gate: Each stage has defined deliverables. Each gate has clear criteria for Go/Kill/Hold/Recycle. Key gates: 1) Idea Screen, 2) Second Screen, 3) Go to Development, 4) Go to Testing, 5) Go to Launch. Document your process and follow it.' },
      { key: 'launch_timeline', title: '🆕 Launch Timeline & Plan', prompt: 'What is your detailed launch timeline? Work backwards from target launch date. What are the critical path items? What could delay you?', guidance: 'CPG launches typically need 12-24 months from concept to shelf. Key milestones: Concept lock, formulation finalized, packaging design approved, first production run, sales sell-in begins, marketing assets ready, first ship date. Build in contingency.' }
    ]
  },
  {
    key: 'northwest',
    compassPoint: 'NW',
    title: 'Money & Sustainability',
    subtitle: 'Financial Health',
    icon: MysticalIcons.northwest,
    color: '#10B981',
    description: 'How you create value and keep going',
    furtherReading: [
      { title: 'Venture Deals (Feld & Mendelson)', note: 'Fundraising basics' },
      { title: 'Profit First (Mike Michalowicz)', note: 'Financial discipline' },
      { title: 'Beyond Entrepreneurship 2.0', note: 'Capital strategy' }
    ],
    addressedIn: ['Community Charter: Stewardship & Renewal'],
    subElements: [
      { key: 'revenue_model', title: 'Revenue Model', prompt: 'How do you make money? Break down your revenue streams.', guidance: 'Revenue = customers × price × frequency. Be specific about each stream.' },
      { key: 'unit_economics', title: 'Unit Economics', prompt: 'What does it cost to get a customer? What\'s a customer worth over time?', guidance: 'Healthy: Lifetime Value > 3× Customer Acquisition Cost.' },
      { key: 'cost_structure', title: 'Cost Structure', prompt: 'What are your major costs? Fixed vs. variable? What scales and what doesn\'t?', guidance: 'Know your burn. Know what costs grow with revenue vs. what doesn\'t.' },
      { key: 'runway_burn', title: 'Runway & Burn', prompt: 'How much are you spending monthly? How long can you last?', guidance: '18-24 months of runway is comfortable. What extends your runway?' },
      { key: 'funding_strategy', title: 'Funding Strategy', prompt: 'Do you need outside money? How much? From whom? For what milestones?', guidance: 'Tie funding to specific milestones. Know your dilution.' },
      { key: 'path_to_sustainability', title: 'Path to Sustainability', prompt: 'When and how do you become self-sustaining? What\'s the path to profitability?', guidance: 'Know when you could be profitable if you had to. Control your destiny.' },
      { key: 'resource_allocation', title: 'Resource Allocation', prompt: 'How do you decide where to invest time, money, and attention?', guidance: 'Strategy is saying no. What are you NOT doing? Where do you focus?' },
      { key: 'cpg_cost_model', title: '🆕 CPG Cost & Margin Model', prompt: 'For CPG: What is your COGS breakdown (ingredients, packaging, labor, overhead)? What\'s your target gross margin? How does margin look across channels (retail vs. DTC)?', guidance: 'CPG margin targets vary by channel: DTC 60-70% GM, Retail 40-50% GM. Understand: Raw materials, packaging, co-packer/mfg costs, freight in/out. Calculate landed cost per unit. Model trade spend impact. Most CPG companies need 35-45% gross margin minimum at retail.' },
      { key: 'supply_chain_ops', title: '🆕 Supply Chain & Operations', prompt: 'How will you manufacture? Co-packer vs. own facility? Ingredient sourcing? Quality control? Inventory management? Fulfillment strategy?', guidance: 'For CPG startups: Co-packers are usually the right choice initially. Consider: Minimum order quantities (MOQs), lead times, quality certifications (SQF, organic, non-GMO), shelf life, cold chain requirements. Plan for scale-up. Have backup suppliers.' }
    ]
  }
];

// ============ OUTPUT OPTIONS ============
const OUTPUT_OPTIONS = [
  { key: 'business_plan', title: 'Business Plan', description: 'Full Beyond Entrepreneurship 2.0 style plan', icon: FileText },
  { key: 'npd_business_plan', title: 'NPD Business Plan', description: 'New Product Development plan with Stage-Gate milestones', icon: FileText },
  { key: 'one_page_summary', title: 'One-Page Summary', description: 'Tight, focused overview', icon: FileText },
  { key: 'pitch_deck_outline', title: 'Pitch Deck Outline', description: 'Slide-by-slide structure for your deck', icon: FileText },
  { key: 'business_model', title: 'Business Model (Collins)', description: 'Beyond Entrepreneurship 2.0 framework', icon: FileText },
  { key: 'executive_summary', title: 'Executive Summary', description: '2-3 paragraph compelling summary', icon: FileText },
  { key: 'expert_analysis', title: 'Expert Analysis', description: 'Candid assessment with success probability', icon: MetatronIcon },
  { key: 'pro_forma', title: '10-Year Pro Forma', description: 'Professional P&L projections', icon: Calculator }
];

// ============ PRO FORMA CONFIGURATION ============
const DEFAULT_PRO_FORMA = {
  // Revenue assumptions
  initialCustomers: 100,
  customerGrowthRate: 35, // % per year
  averageRevenue: 1200, // $ per customer per year
  revenueGrowthRate: 10, // % per year (price increases, upsells)
  
  // Cost assumptions
  initialCogs: 30, // % of revenue
  cogsImprovement: 2, // % reduction per year
  initialOpex: 150000, // $ fixed operational expenses
  opexGrowthRate: 15, // % per year
  
  // Team & overhead
  initialHeadcount: 5,
  avgSalary: 85000,
  headcountGrowth: 20, // % per year
  
  // Other
  taxRate: 25,
  startingCash: 500000
};

// Calculate 10-year projections
const calculateProForma = (assumptions) => {
  const years = [];
  let customers = assumptions.initialCustomers;
  let avgRevenue = assumptions.averageRevenue;
  let cogsPercent = assumptions.initialCogs;
  let opex = assumptions.initialOpex;
  let headcount = assumptions.initialHeadcount;
  let cumulativeCash = assumptions.startingCash;
  
  for (let year = 1; year <= 10; year++) {
    // Revenue
    const grossRevenue = Math.round(customers * avgRevenue);
    
    // COGS
    const cogs = Math.round(grossRevenue * (cogsPercent / 100));
    const grossProfit = grossRevenue - cogs;
    const grossMargin = ((grossProfit / grossRevenue) * 100).toFixed(1);
    
    // Operating expenses
    const salaries = Math.round(headcount * assumptions.avgSalary);
    const totalOpex = opex + salaries;
    
    // EBITDA
    const ebitda = grossProfit - totalOpex;
    const ebitdaMargin = ((ebitda / grossRevenue) * 100).toFixed(1);
    
    // Net income (simplified - no interest/depreciation for MVP)
    const taxExpense = ebitda > 0 ? Math.round(ebitda * (assumptions.taxRate / 100)) : 0;
    const netIncome = ebitda - taxExpense;
    const netMargin = ((netIncome / grossRevenue) * 100).toFixed(1);
    
    // Cash position
    cumulativeCash += netIncome;
    
    years.push({
      year,
      customers: Math.round(customers),
      avgRevenue: Math.round(avgRevenue),
      grossRevenue,
      cogs,
      grossProfit,
      grossMargin: parseFloat(grossMargin),
      salaries,
      opex,
      totalOpex,
      ebitda,
      ebitdaMargin: parseFloat(ebitdaMargin),
      taxExpense,
      netIncome,
      netMargin: parseFloat(netMargin),
      headcount: Math.round(headcount),
      cumulativeCash
    });
    
    // Grow for next year
    customers *= (1 + assumptions.customerGrowthRate / 100);
    avgRevenue *= (1 + assumptions.revenueGrowthRate / 100);
    cogsPercent = Math.max(15, cogsPercent - assumptions.cogsImprovement);
    opex *= (1 + assumptions.opexGrowthRate / 100);
    headcount *= (1 + assumptions.headcountGrowth / 100);
  }
  
  return years;
};

// Format currency
const formatCurrency = (value, compact = false) => {
  if (compact) {
    if (Math.abs(value) >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// ============ PRO FORMA MODAL COMPONENT (Enhanced) ============
const ProFormaModal = ({ isOpen, onClose, responses }) => {
  const [assumptions, setAssumptions] = useState(DEFAULT_PRO_FORMA);
  const [projections, setProjections] = useState([]);
  const [activeView, setActiveView] = useState('dashboard'); // 'dashboard', 'table', 'share'
  const [customLabels, setCustomLabels] = useState({
    customers: 'Customers',
    revenue: 'Gross Revenue',
    cogs: 'Cost of Goods Sold',
    salaries: 'Team Costs',
    opex: 'Operating Expenses'
  });
  const [companyName, setCompanyName] = useState('');
  const [shareCode, setShareCode] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  
  // Extract company info from responses
  useEffect(() => {
    if (isOpen) {
      // Try to find company/venture name from responses
      const nameFields = ['north_origin_story', 'east_key_message', 'east_brand_voice'];
      for (const field of nameFields) {
        if (responses[field]) {
          // Extract potential company name from first response
          const firstLine = responses[field].split('\n')[0];
          if (firstLine.length < 50) {
            setCompanyName(firstLine);
            break;
          }
        }
      }
      
      // Extract pricing info if available
      if (responses['southwest_pricing_value']) {
        const pricing = responses['southwest_pricing_value'];
        const match = pricing.match(/\$(\d+[\d,]*)/);
        if (match) {
          const price = parseInt(match[1].replace(/,/g, ''));
          if (price > 0) {
            setAssumptions(prev => ({ ...prev, averageRevenue: price }));
          }
        }
      }
      
      // Extract team size if available
      if (responses['northeast_founding_team'] || responses['northeast_key_seats']) {
        const teamText = (responses['northeast_founding_team'] || '') + ' ' + (responses['northeast_key_seats'] || '');
        const numbers = teamText.match(/\d+/g);
        if (numbers && numbers.length > 0) {
          const potentialTeamSize = Math.min(20, Math.max(1, parseInt(numbers[0])));
          setAssumptions(prev => ({ ...prev, initialHeadcount: potentialTeamSize }));
        }
      }
      
      // Extract market size if available
      if (responses['southeast_market_size']) {
        const marketText = responses['southeast_market_size'].toLowerCase();
        if (marketText.includes('saas') || marketText.includes('software') || marketText.includes('subscription')) {
          setCustomLabels(prev => ({ ...prev, customers: 'Subscribers', revenue: 'MRR × 12' }));
        } else if (marketText.includes('member') || marketText.includes('community')) {
          setCustomLabels(prev => ({ ...prev, customers: 'Members', revenue: 'Membership Revenue' }));
        }
      }
      
      setProjections(calculateProForma(assumptions));
    }
  }, [isOpen, responses]);
  
  useEffect(() => {
    if (isOpen) {
      setProjections(calculateProForma(assumptions));
    }
  }, [isOpen, assumptions]);
  
  const updateAssumption = (key, value) => {
    setAssumptions(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
  };
  
  const updateLabel = (key, value) => {
    setCustomLabels(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  const handleShare = async () => {
    setIsSharing(true);
    try {
      const response = await fetch(`${API_URL}/api/share/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_type: 'pro_forma',
          title: '10-Year Pro Forma',
          company_name: companyName,
          content: { responses },
          assumptions,
          projections,
          custom_labels: customLabels,
          branding: {}
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setShareCode(data.share_code);
        try {
          await navigator.clipboard.writeText(data.share_url);
          toast.success('Share link copied to clipboard!');
        } catch (clipboardError) {
          toast.success('Share link created! Copy it from below.');
        }
      }
    } catch (error) {
      toast.error('Failed to create share link');
    } finally {
      setIsSharing(false);
    }
  };
  
  if (!isOpen) return null;
  
  const maxRevenue = Math.max(...projections.map(p => p.grossRevenue));
  const maxNetIncome = Math.max(...projections.map(p => Math.abs(p.netIncome)));
  const breakEvenYear = projections.findIndex(p => p.netIncome > 0) + 1;
  const totalRevenue = projections.reduce((sum, p) => sum + p.grossRevenue, 0);
  const totalProfit = projections.reduce((sum, p) => sum + p.netIncome, 0);
  const year10 = projections[9] || {};
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-7xl max-h-[95vh] overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-white/20 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10 bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-amber-600/20">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <Calculator size={24} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your Company Name"
                  className="text-xl font-bold text-white bg-transparent border-none outline-none placeholder:text-white/40"
                />
                <Badge className="bg-amber-500/20 text-amber-300 text-xs font-normal">Pro Forma</Badge>
              </div>
              <p className="text-sm text-white/60">10-Year Financial Projections • Investment Ready</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex bg-white/10 rounded-lg p-1">
              {['dashboard', 'table', 'share'].map((view) => (
                <button
                  key={view}
                  onClick={() => setActiveView(view)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all capitalize ${
                    activeView === view 
                      ? 'bg-white/20 text-white' 
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  {view}
                </button>
              ))}
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <X size={20} />
            </Button>
          </div>
        </div>
        
        <div className="flex max-h-[calc(95vh-80px)]">
          {/* Left Panel - Assumptions */}
          <div className="w-80 border-r border-white/10 bg-black/20 p-5 overflow-auto">
            <h3 className="text-sm font-semibold text-white/80 mb-4 flex items-center gap-2">
              <AncientSunIcon size={16} className="text-amber-400" />
              Model Assumptions
            </h3>
            
            <div className="space-y-5">
              {/* Revenue Section */}
              <div className="space-y-3">
                <h4 className="text-xs font-medium text-indigo-400 uppercase tracking-wider">Revenue Drivers</h4>
                <div className="space-y-2">
                  <label className="block">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-white/60">Customer Label</span>
                    </div>
                    <input
                      type="text"
                      value={customLabels.customers}
                      onChange={(e) => updateLabel('customers', e.target.value)}
                      className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-white/60">Initial {customLabels.customers} (Year 1)</span>
                    <input
                      type="number"
                      value={assumptions.initialCustomers}
                      onChange={(e) => updateAssumption('initialCustomers', e.target.value)}
                      className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-white/60">{customLabels.customers} Growth Rate (%/year)</span>
                    <input
                      type="number"
                      value={assumptions.customerGrowthRate}
                      onChange={(e) => updateAssumption('customerGrowthRate', e.target.value)}
                      className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-white/60">Avg Revenue per {customLabels.customers.slice(0, -1)} ($/year)</span>
                    <input
                      type="number"
                      value={assumptions.averageRevenue}
                      onChange={(e) => updateAssumption('averageRevenue', e.target.value)}
                      className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                    />
                  </label>
                </div>
              </div>
              
              {/* Costs Section */}
              <div className="space-y-3">
                <h4 className="text-xs font-medium text-emerald-400 uppercase tracking-wider">Cost Structure</h4>
                <div className="space-y-2">
                  <label className="block">
                    <span className="text-xs text-white/60">Initial COGS (% of revenue)</span>
                    <input
                      type="number"
                      value={assumptions.initialCogs}
                      onChange={(e) => updateAssumption('initialCogs', e.target.value)}
                      className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-white/60">Fixed Opex (Year 1, $)</span>
                    <input
                      type="number"
                      value={assumptions.initialOpex}
                      onChange={(e) => updateAssumption('initialOpex', e.target.value)}
                      className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                    />
                  </label>
                </div>
              </div>
              
              {/* Team Section */}
              <div className="space-y-3">
                <h4 className="text-xs font-medium text-purple-400 uppercase tracking-wider">Team & Overhead</h4>
                <div className="space-y-2">
                  <label className="block">
                    <span className="text-xs text-white/60">Initial Headcount</span>
                    <input
                      type="number"
                      value={assumptions.initialHeadcount}
                      onChange={(e) => updateAssumption('initialHeadcount', e.target.value)}
                      className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-white/60">Avg Salary ($)</span>
                    <input
                      type="number"
                      value={assumptions.avgSalary}
                      onChange={(e) => updateAssumption('avgSalary', e.target.value)}
                      className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-white/60">Headcount Growth (%/year)</span>
                    <input
                      type="number"
                      value={assumptions.headcountGrowth}
                      onChange={(e) => updateAssumption('headcountGrowth', e.target.value)}
                      className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                  </label>
                </div>
              </div>
              
              {/* Other Section */}
              <div className="space-y-3">
                <h4 className="text-xs font-medium text-amber-400 uppercase tracking-wider">Other</h4>
                <div className="space-y-2">
                  <label className="block">
                    <span className="text-xs text-white/60">Starting Cash ($)</span>
                    <input
                      type="number"
                      value={assumptions.startingCash}
                      onChange={(e) => updateAssumption('startingCash', e.target.value)}
                      className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-white/60">Tax Rate (%)</span>
                    <input
                      type="number"
                      value={assumptions.taxRate}
                      onChange={(e) => updateAssumption('taxRate', e.target.value)}
                      className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Panel - Projections */}
          <div className="flex-1 p-5 overflow-auto bg-gradient-to-br from-slate-900/50 to-black/50">
            {activeView === 'dashboard' && (
              <div className="space-y-6">
                {/* Key Metrics Summary */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-indigo-500/20 to-indigo-600/10 rounded-xl p-4 border border-indigo-500/30">
                    <p className="text-xs text-indigo-300 font-medium">Year 10 Revenue</p>
                    <p className="text-2xl font-bold text-white mt-1">{formatCurrency(year10.grossRevenue || 0, true)}</p>
                    <p className="text-xs text-indigo-300/70 mt-1">{(year10.customers || 0).toLocaleString()} {customLabels.customers.toLowerCase()}</p>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 rounded-xl p-4 border border-emerald-500/30">
                    <p className="text-xs text-emerald-300 font-medium">Year 10 Net Income</p>
                    <p className={`text-2xl font-bold mt-1 ${(year10.netIncome || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatCurrency(year10.netIncome || 0, true)}
                    </p>
                    <p className="text-xs text-emerald-300/70 mt-1">{year10.netMargin}% margin</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-xl p-4 border border-purple-500/30">
                    <p className="text-xs text-purple-300 font-medium">10-Year Total</p>
                    <p className={`text-2xl font-bold mt-1 ${totalProfit >= 0 ? 'text-purple-400' : 'text-red-400'}`}>
                      {formatCurrency(totalProfit, true)}
                    </p>
                    <p className="text-xs text-purple-300/70 mt-1">cumulative profit</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-xl p-4 border border-amber-500/30">
                    <p className="text-xs text-amber-300 font-medium">Break-Even</p>
                    <p className="text-2xl font-bold text-white mt-1">
                      {breakEvenYear > 0 ? `Year ${breakEvenYear}` : 'N/A'}
                    </p>
                    <p className="text-xs text-amber-300/70 mt-1">first profitable year</p>
                  </div>
                </div>
                
                {/* Beautiful Charts */}
                <div className="bg-black/30 rounded-xl border border-white/10 p-6">
                  <h4 className="text-sm font-semibold text-white mb-6">Revenue & Profitability Over 10 Years</h4>
                  
                  {/* Revenue Chart */}
                  <div className="space-y-4 mb-8">
                    <p className="text-xs text-indigo-300 font-medium uppercase tracking-wider">{customLabels.revenue}</p>
                    <div className="flex items-end gap-2 h-40">
                      {projections.map((p, i) => (
                        <div key={p.year} className="flex-1 flex flex-col items-center gap-2">
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: `${(p.grossRevenue / maxRevenue) * 100}%` }}
                            transition={{ delay: i * 0.05, duration: 0.5 }}
                            className="w-full bg-gradient-to-t from-indigo-600 to-indigo-400 rounded-t-lg relative group cursor-pointer"
                          >
                            <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-800 px-2 py-1 rounded text-xs text-white whitespace-nowrap z-10">
                              {formatCurrency(p.grossRevenue, true)}
                            </div>
                          </motion.div>
                          <span className="text-xs text-white/50">Y{p.year}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Net Income Chart */}
                  <div className="space-y-4">
                    <p className="text-xs text-emerald-300 font-medium uppercase tracking-wider">Net Income</p>
                    <div className="flex items-end gap-2 h-32">
                      {projections.map((p, i) => {
                        const isPositive = p.netIncome >= 0;
                        const heightPercent = (Math.abs(p.netIncome) / maxNetIncome) * 100;
                        return (
                          <div key={p.year} className="flex-1 flex flex-col items-center gap-2">
                            <motion.div
                              initial={{ height: 0 }}
                              animate={{ height: `${heightPercent}%` }}
                              transition={{ delay: i * 0.05 + 0.3, duration: 0.5 }}
                              className={`w-full rounded-t-lg relative group cursor-pointer ${
                                isPositive 
                                  ? 'bg-gradient-to-t from-emerald-600 to-emerald-400' 
                                  : 'bg-gradient-to-t from-red-600 to-red-400'
                              }`}
                            >
                              <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-800 px-2 py-1 rounded text-xs text-white whitespace-nowrap z-10">
                                {formatCurrency(p.netIncome, true)}
                              </div>
                            </motion.div>
                            <span className="text-xs text-white/50">Y{p.year}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                
                {/* Quick Stats Row */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-white">{formatCurrency(totalRevenue, true)}</p>
                    <p className="text-xs text-white/50 mt-1">10-Year Total Revenue</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-white">{year10.headcount}</p>
                    <p className="text-xs text-white/50 mt-1">Year 10 Team Size</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-white">{Math.round(((year10.grossRevenue / projections[0]?.grossRevenue) - 1) * 100 || 0)}%</p>
                    <p className="text-xs text-white/50 mt-1">Revenue Growth (Y1→Y10)</p>
                  </div>
                </div>
              </div>
            )}
            
            {activeView === 'table' && (
              <div className="space-y-6">
                {/* Main P&L Table */}
                <div className="bg-black/30 rounded-xl border border-white/10 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="pro-forma-table">
                      <thead>
                        <tr className="bg-gradient-to-r from-indigo-600/30 via-purple-600/20 to-amber-600/30">
                          <th className="text-left py-3 px-4 text-white font-semibold sticky left-0 bg-slate-900/95 backdrop-blur border-r border-white/10 min-w-[160px]">
                            P&L Line Item
                          </th>
                          {projections.map((p) => (
                            <th key={p.year} className="text-right py-3 px-3 text-white font-semibold min-w-[100px]">
                              Year {p.year}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        <tr className="bg-indigo-500/5">
                          <td className="py-3 px-4 text-indigo-300 font-medium sticky left-0 bg-slate-900/95 border-r border-white/10">{customLabels.customers}</td>
                          {projections.map((p) => (
                            <td key={p.year} className="text-right py-3 px-3 text-white/80 font-mono">{p.customers.toLocaleString()}</td>
                          ))}
                        </tr>
                        <tr className="bg-indigo-500/10 font-semibold">
                          <td className="py-3 px-4 text-indigo-200 sticky left-0 bg-slate-900/95 border-r border-white/10">{customLabels.revenue}</td>
                          {projections.map((p) => (
                            <td key={p.year} className="text-right py-3 px-3 text-indigo-300 font-mono">{formatCurrency(p.grossRevenue, true)}</td>
                          ))}
                        </tr>
                        <tr className="bg-red-500/5">
                          <td className="py-3 px-4 text-red-300/80 sticky left-0 bg-slate-900/95 border-r border-white/10">{customLabels.cogs}</td>
                          {projections.map((p) => (
                            <td key={p.year} className="text-right py-3 px-3 text-red-300/80 font-mono">({formatCurrency(p.cogs, true)})</td>
                          ))}
                        </tr>
                        <tr className="bg-emerald-500/10 font-semibold">
                          <td className="py-3 px-4 text-emerald-200 sticky left-0 bg-slate-900/95 border-r border-white/10">Gross Profit</td>
                          {projections.map((p) => (
                            <td key={p.year} className="text-right py-3 px-3 text-emerald-300 font-mono">
                              {formatCurrency(p.grossProfit, true)}
                              <span className="text-xs ml-1 text-emerald-400/60">({p.grossMargin}%)</span>
                            </td>
                          ))}
                        </tr>
                        <tr className="bg-purple-500/5">
                          <td className="py-3 px-4 text-purple-300/80 sticky left-0 bg-slate-900/95 border-r border-white/10">{customLabels.salaries}</td>
                          {projections.map((p) => (
                            <td key={p.year} className="text-right py-3 px-3 text-purple-300/80 font-mono">({formatCurrency(p.salaries, true)})</td>
                          ))}
                        </tr>
                        <tr className="bg-purple-500/5">
                          <td className="py-3 px-4 text-purple-300/80 sticky left-0 bg-slate-900/95 border-r border-white/10">{customLabels.opex}</td>
                          {projections.map((p) => (
                            <td key={p.year} className="text-right py-3 px-3 text-purple-300/80 font-mono">({formatCurrency(p.opex, true)})</td>
                          ))}
                        </tr>
                        <tr className="bg-amber-500/10 font-semibold">
                          <td className="py-3 px-4 text-amber-200 sticky left-0 bg-slate-900/95 border-r border-white/10">EBITDA</td>
                          {projections.map((p) => (
                            <td key={p.year} className={`text-right py-3 px-3 font-mono ${p.ebitda >= 0 ? 'text-amber-300' : 'text-red-400'}`}>
                              {formatCurrency(p.ebitda, true)}
                            </td>
                          ))}
                        </tr>
                        <tr className="bg-slate-500/5">
                          <td className="py-3 px-4 text-slate-400 sticky left-0 bg-slate-900/95 border-r border-white/10">Taxes</td>
                          {projections.map((p) => (
                            <td key={p.year} className="text-right py-3 px-3 text-slate-400 font-mono">({formatCurrency(p.taxExpense, true)})</td>
                          ))}
                        </tr>
                        <tr className="bg-gradient-to-r from-emerald-500/20 via-emerald-500/10 to-emerald-500/20 font-bold">
                          <td className="py-4 px-4 text-emerald-100 sticky left-0 bg-slate-900/95 border-r border-white/10 text-base">Net Income</td>
                          {projections.map((p) => (
                            <td key={p.year} className={`text-right py-4 px-3 font-mono text-base ${p.netIncome >= 0 ? 'text-emerald-300' : 'text-red-400'}`}>
                              {formatCurrency(p.netIncome, true)}
                            </td>
                          ))}
                        </tr>
                        <tr className="bg-cyan-500/10">
                          <td className="py-3 px-4 text-cyan-200 font-semibold sticky left-0 bg-slate-900/95 border-r border-white/10">Cumulative Cash</td>
                          {projections.map((p) => (
                            <td key={p.year} className={`text-right py-3 px-3 font-mono ${p.cumulativeCash >= 0 ? 'text-cyan-300' : 'text-red-400'}`}>
                              {formatCurrency(p.cumulativeCash, true)}
                            </td>
                          ))}
                        </tr>
                        <tr className="bg-slate-500/5">
                          <td className="py-3 px-4 text-slate-300 sticky left-0 bg-slate-900/95 border-r border-white/10">Team Size</td>
                          {projections.map((p) => (
                            <td key={p.year} className="text-right py-3 px-3 text-slate-300 font-mono">{p.headcount}</td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-xs text-white/40 px-2">
                  <p>* All figures in USD. Projections based on stated assumptions.</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      const headers = ['Line Item', ...projections.map(p => `Year ${p.year}`)].join(',');
                      const rows = [
                        [customLabels.customers, ...projections.map(p => p.customers)],
                        [customLabels.revenue, ...projections.map(p => p.grossRevenue)],
                        [customLabels.cogs, ...projections.map(p => -p.cogs)],
                        ['Gross Profit', ...projections.map(p => p.grossProfit)],
                        [customLabels.salaries, ...projections.map(p => -p.salaries)],
                        [customLabels.opex, ...projections.map(p => -p.opex)],
                        ['EBITDA', ...projections.map(p => p.ebitda)],
                        ['Taxes', ...projections.map(p => -p.taxExpense)],
                        ['Net Income', ...projections.map(p => p.netIncome)],
                        ['Cumulative Cash', ...projections.map(p => p.cumulativeCash)],
                        ['Headcount', ...projections.map(p => p.headcount)],
                      ].map(row => row.join(',')).join('\n');
                      const csv = headers + '\n' + rows;
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${companyName || 'proforma'}_10year.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success('Pro Forma exported to CSV!');
                    }}
                    className="text-white/60 hover:text-white hover:bg-white/10"
                  >
                    <Download size={14} className="mr-1" />
                    Export CSV
                  </Button>
                </div>
              </div>
            )}
            
            {activeView === 'share' && (
              <div className="space-y-6 max-w-xl mx-auto py-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-indigo-500/30">
                    <ExternalLink size={28} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Share Your Pro Forma</h3>
                  <p className="text-white/60 text-sm">Create a beautiful, read-only link to share with investors, partners, or advisors.</p>
                </div>
                
                {shareCode ? (
                  <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                    <p className="text-xs text-white/50 uppercase tracking-wider mb-2">Your Share Link</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-black/30 px-4 py-3 rounded-lg text-indigo-300 font-mono text-sm overflow-hidden">
                        {`${window.location.origin}/share/${shareCode}`}
                      </code>
                      <Button
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/share/${shareCode}`);
                          toast.success('Link copied!');
                        }}
                        className="bg-indigo-500 hover:bg-indigo-600"
                      >
                        <Copy size={14} />
                      </Button>
                    </div>
                    <p className="text-xs text-white/40 mt-3">Share code: <span className="font-mono text-amber-300">{shareCode}</span></p>
                  </div>
                ) : (
                  <Button
                    onClick={handleShare}
                    disabled={isSharing}
                    className="w-full py-6 text-lg bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                  >
                    {isSharing ? (
                      <>
                        <RefreshCw size={18} className="mr-2 animate-spin" />
                        Creating Link...
                      </>
                    ) : (
                      <>
                        <ExternalLink size={18} className="mr-2" />
                        Generate Share Link
                      </>
                    )}
                  </Button>
                )}
                
                <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/20">
                  <p className="text-amber-300 text-sm font-medium mb-1">What viewers will see:</p>
                  <ul className="text-amber-300/70 text-xs space-y-1">
                    <li>• Beautiful dashboard with key metrics</li>
                    <li>• Interactive charts (revenue & profitability)</li>
                    <li>• Full 10-year P&L table</li>
                    <li>• Your model assumptions</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============ COMPASS NAVIGATION COMPONENTS ============
const CompassNavSidebar = ({ sections, activeSection, onSectionClick, responses }) => {
  const getCompletion = (sectionKey) => {
    const section = sections.find(s => s.key === sectionKey);
    if (!section) return 0;
    const answered = section.subElements.filter(sub => 
      responses[`${sectionKey}_${sub.key}`]?.trim()
    ).length;
    return Math.round((answered / section.subElements.length) * 100);
  };

  return (
    <div className="space-y-2">
      {sections.map((section) => {
        const completion = getCompletion(section.key);
        const isActive = activeSection === section.key;
        
        return (
          <motion.button
            key={section.key}
            onClick={() => onSectionClick(section.key)}
            className={`w-full p-3 rounded-xl text-left transition-all ${
              isActive 
                ? 'bg-white/10 border border-white/20' 
                : 'bg-white/5 hover:bg-white/10 border border-transparent'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: section.color + '30' }}
              >
                <span className="font-bold text-sm" style={{ color: section.color }}>
                  {section.compassPoint}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className={`font-medium text-sm truncate ${isActive ? 'text-white' : 'text-white/80'}`}>
                  {section.title}
                </h4>
                <p className="text-xs text-white/50 truncate">{section.subtitle}</p>
              </div>
              <div className="text-right">
                <span className="text-xs font-medium" style={{ color: section.color }}>
                  {completion}%
                </span>
              </div>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
};

// Business Compass Rose Component
const BusinessCompassRose = ({ sections, activeSection, onSectionClick, size = 200, responses }) => {
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size * 0.38;

  const angles = {
    north: -90,
    northeast: -45,
    east: 0,
    southeast: 45,
    south: 90,
    southwest: 135,
    west: 180,
    northwest: -135
  };

  const getCompletion = (sectionKey) => {
    const section = sections.find(s => s.key === sectionKey);
    if (!section) return 0;
    const answered = section.subElements.filter(sub => 
      responses[`${sectionKey}_${sub.key}`]?.trim()
    ).length;
    return Math.round((answered / section.subElements.length) * 100);
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full">
        {/* Background circles */}
        <circle cx={centerX} cy={centerY} r={radius + 20} fill="none" stroke="white" strokeWidth="0.5" opacity="0.1" />
        <circle cx={centerX} cy={centerY} r={radius} fill="none" stroke="white" strokeWidth="1" opacity="0.2" />
        <circle cx={centerX} cy={centerY} r={radius - 30} fill="none" stroke="white" strokeWidth="0.5" opacity="0.1" />
        
        {/* Compass lines */}
        {Object.entries(angles).map(([key, angle]) => {
          const rad = (angle * Math.PI) / 180;
          const x2 = centerX + Math.cos(rad) * (radius + 15);
          const y2 = centerY + Math.sin(rad) * (radius + 15);
          return (
            <line
              key={key}
              x1={centerX}
              y1={centerY}
              x2={x2}
              y2={y2}
              stroke="white"
              strokeWidth="0.5"
              opacity="0.15"
            />
          );
        })}

        {/* Center dot */}
        <circle cx={centerX} cy={centerY} r="4" fill={STARTUP_THEME.primary} />
      </svg>

      {/* Section buttons */}
      {sections.map((section) => {
        const angle = angles[section.key];
        const rad = (angle * Math.PI) / 180;
        const x = centerX + Math.cos(rad) * radius;
        const y = centerY + Math.sin(rad) * radius;
        const isActive = activeSection === section.key;
        const completion = getCompletion(section.key);

        return (
          <motion.button
            key={section.key}
            className={`absolute transform -translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center transition-all ${
              isActive ? 'ring-2 ring-white ring-opacity-50' : ''
            }`}
            style={{
              left: x,
              top: y,
              width: 36,
              height: 36,
              backgroundColor: isActive ? section.color : section.color + '40',
              border: `2px solid ${section.color}`,
            }}
            onClick={() => onSectionClick(section.key)}
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.95 }}
            title={`${section.title} (${completion}%)`}
          >
            <span className="font-bold text-xs text-white">{section.compassPoint}</span>
          </motion.button>
        );
      })}
    </div>
  );
};

// ============ MAIN COMPONENT ============
const StartupStudio = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');  // Get project ID from URL query params
  const { isAuthenticated, user } = useAuth();
  const [activeSection, setActiveSection] = useState('north');
  const [activeSubElement, setActiveSubElement] = useState('origin_story');
  const [responses, setResponses] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toolsPanelCollapsed, setToolsPanelCollapsed] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [showToolsPanel, setShowToolsPanel] = useState(false);
  const [showSignInBanner, setShowSignInBanner] = useState(true);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [showOutputOptions, setShowOutputOptions] = useState(false);
  const [selectedOutput, setSelectedOutput] = useState('business_plan');
  const [showReadingModal, setShowReadingModal] = useState(false);
  const [showProFormaModal, setShowProFormaModal] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [mobileViewMode, setMobileViewMode] = useState('vertical'); // 'vertical' or 'focused'
  // Auto Generate state
  const [showAutoGenerate, setShowAutoGenerate] = useState(false);
  const [autoGenDescription, setAutoGenDescription] = useState('');
  const [isAutoGenerating, setIsAutoGenerating] = useState(false);
  // Document sharing state
  const [isSharingDocument, setIsSharingDocument] = useState(false);
  const [documentShareUrl, setDocumentShareUrl] = useState(null);
  // Brand Checker state
  const [showBrandChecker, setShowBrandChecker] = useState(false);
  // Tasks state (for cross-reactivity)
  const [tasks, setTasks] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('startup_studio_tasks') || '[]');
    } catch { return []; }
  });

  // Save tasks when they change
  useEffect(() => {
    localStorage.setItem('startup_studio_tasks', JSON.stringify(tasks));
  }, [tasks]);

  // Add task handler (for cross-reactivity) - now saves to backend
  const handleAddTask = async (task) => {
    const newTask = {
      id: Date.now().toString(),
      title: task.title || task,
      category: task.category || 'general',
      priority: task.priority || 'medium',
      completed: false,
      createdAt: new Date().toISOString(),
      source: task.source || 'manual'
    };
    
    // Add to local state immediately for responsiveness
    setTasks(prev => [...prev, newTask]);
    toast.success('Task added!');
    
    // Also save to backend if API is available
    if (API_URL) {
      try {
        const response = await fetch(`${API_URL}/api/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: newTask.title,
            category: newTask.category,
            priority: newTask.priority,
            project_id: projectId || null
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          // Update local task with backend task_id
          setTasks(prev => prev.map(t => 
            t.id === newTask.id ? { ...t, task_id: data.task.task_id } : t
          ));
        }
      } catch (error) {
        // Silently fail - task is already in local state
        console.log('Task saved locally only');
      }
    }
  };

  // Load saved responses
  useEffect(() => {
    const savedResponses = localStorage.getItem('startup_studio_responses');
    if (savedResponses) {
      try {
        setResponses(JSON.parse(savedResponses));
      } catch (e) {
        console.error('Failed to load saved responses');
      }
    }
  }, []);

  // Load tasks from backend - with proper error handling
  useEffect(() => {
    const loadTasks = async () => {
      // Skip if API_URL is not configured properly
      if (!API_URL) {
        console.log('API_URL not configured, using local tasks only');
        return;
      }
      
      try {
        const url = projectId 
          ? `${API_URL}/api/tasks?project_id=${projectId}`
          : `${API_URL}/api/tasks`;
        const response = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.tasks && data.tasks.length > 0) {
            setTasks(data.tasks.map(t => ({
              id: t.task_id,
              task_id: t.task_id,
              title: t.title,
              category: t.category,
              priority: t.priority,
              completed: t.completed,
              createdAt: t.created_at
            })));
          }
        }
      } catch (error) {
        // Silently fail - tasks will just use local storage
        console.log('Tasks API not available, using local storage');
      }
    };
    loadTasks();
  }, [projectId]);

  // Save responses when they change
  useEffect(() => {
    if (Object.keys(responses).length > 0) {
      localStorage.setItem('startup_studio_responses', JSON.stringify(responses));
    }
  }, [responses]);

  const currentSection = STARTUP_SECTIONS.find(s => s.key === activeSection);
  const currentSubElement = currentSection?.subElements.find(se => se.key === activeSubElement);
  
  const answeredSubElements = Object.keys(responses).filter(k => responses[k]?.trim()).length;
  const totalSubElements = STARTUP_SECTIONS.reduce((acc, s) => acc + s.subElements.length, 0);
  const overallProgress = Math.round((answeredSubElements / totalSubElements) * 100);

  const handleContentChange = (value) => {
    const key = `${activeSection}_${activeSubElement}`;
    setResponses(prev => ({ ...prev, [key]: value }));
  };

  const getCurrentContent = () => {
    return responses[`${activeSection}_${activeSubElement}`] || '';
  };

  const handleSectionClick = (sectionKey) => {
    setActiveSection(sectionKey);
    const section = STARTUP_SECTIONS.find(s => s.key === sectionKey);
    if (section && section.subElements.length > 0) {
      setActiveSubElement(section.subElements[0].key);
    }
    setShowMobileNav(false);
  };

  const handleInsertFromAI = (text) => {
    const currentContent = getCurrentContent();
    const newContent = currentContent ? `${currentContent}\n\n${text}` : text;
    handleContentChange(newContent);
    toast.success('Added to your response');
  };

  const generateOutput = async (outputType) => {
    // Special handling for Pro Forma - open the interactive modal
    if (outputType === 'pro_forma') {
      setShowProFormaModal(true);
      setShowOutputOptions(false);
      return;
    }
    
    if (Object.keys(responses).filter(k => responses[k]?.trim()).length === 0) {
      toast.error('Please answer at least one question first');
      return;
    }
    
    setIsGenerating(true);
    setSelectedOutput(outputType);
    
    try {
      const response = await fetch(`${API_URL}/api/startup/generate-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          responses, 
          output_type: outputType,
          proForma: {}, 
          projections: [] 
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setGeneratedPlan(data);
        setShowPlanModal(true);
        setShowOutputOptions(false);
        toast.success('Document generated!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to generate');
      }
    } catch (error) {
      console.error('Generate error:', error);
      toast.error('Failed to generate. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Auto Generate function
  const handleAutoGenerate = async (mode = 'sections') => {
    if (!autoGenDescription.trim()) {
      toast.error('Please describe your startup first');
      return;
    }
    
    setIsAutoGenerating(true);
    
    try {
      const endpoint = mode === 'document' ? '/api/auto-generate/document' : '/api/auto-generate';
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: autoGenDescription,
          type: 'startup',
          output_format: mode
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (mode === 'sections' && data.generated_content && !data.generated_content.parse_error) {
          // Fill in all the compass sections
          setResponses(data.generated_content);
          setShowAutoGenerate(false);
          toast.success(`Generated ${Object.keys(data.generated_content).length} sections! Review and edit as needed.`);
        } else if (mode === 'document') {
          // Show the generated document
          setGeneratedPlan({ content: data.document, type: 'one_pager' });
          setShowPlanModal(true);
          setShowAutoGenerate(false);
          toast.success('One-pager generated!');
        } else {
          toast.error('Generation returned unexpected format');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to generate');
      }
    } catch (error) {
      console.error('Auto-generate error:', error);
      toast.error('Failed to auto-generate. Please try again.');
    } finally {
      setIsAutoGenerating(false);
    }
  };

  // Share generated document
  const handleShareDocument = async () => {
    if (!generatedPlan?.content) return;
    
    setIsSharingDocument(true);
    try {
      // Get company name from responses
      const companyName = responses.north_origin_story?.split(' ').slice(0, 3).join(' ') || 
                         responses.east_brand_story?.split(' ').slice(0, 3).join(' ') ||
                         'Business Plan';
      
      const response = await fetch(`${API_URL}/api/share/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_type: selectedOutput || 'business_plan',
          title: OUTPUT_OPTIONS.find(o => o.key === selectedOutput)?.title || 'Business Plan',
          company_name: companyName,
          content: generatedPlan.content,
          branding: {},
          metadata: {
            word_count: generatedPlan.word_count,
            generated_at: generatedPlan.generated_at,
            output_type: selectedOutput
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setDocumentShareUrl(data.share_url);
        try {
          await navigator.clipboard.writeText(data.share_url);
          toast.success('Share link copied! Anyone with the link can view your document.');
        } catch (clipboardError) {
          toast.success('Share link created! Copy it from below.');
        }
      } else {
        toast.error('Failed to create share link');
      }
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Failed to share document');
    } finally {
      setIsSharingDocument(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: STARTUP_THEME.background }}>
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-white/10" style={{ background: 'rgba(15, 23, 42, 0.95)' }}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer group" onClick={() => navigate('/')}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-amber-400 group-hover:scale-105 transition-all" style={{ background: 'linear-gradient(135deg, rgba(79,70,229,0.4), rgba(245,158,11,0.4))' }}>
                <MysticalIcons.center className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-heading text-lg font-semibold text-white group-hover:text-amber-300 transition-colors">Startup Studio</h1>
                  <Badge className="bg-indigo-500/20 text-indigo-300 text-[10px]">Business Planning</Badge>
                </div>
                <p className="text-xs text-white/50">{overallProgress}% Complete • 8-Point Compass</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Quick Tool Icons - Premium styling */}
              <div className="flex items-center gap-2 mr-3">
                {/* Brand Checker Icon */}
                <button
                  onClick={() => setShowBrandChecker(true)}
                  className="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 via-orange-500/15 to-yellow-500/20 border border-amber-400/30 hover:border-amber-400/60 hover:from-amber-500/30 hover:via-orange-500/25 hover:to-yellow-500/30 transition-all duration-300 flex items-center justify-center shadow-lg shadow-amber-500/10 hover:shadow-amber-500/20"
                  title="Brand Name Checker"
                  data-testid="brand-checker-icon"
                >
                  <BrandCheckerIcon size={20} className="text-amber-300 group-hover:text-amber-200 transition-colors" />
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-amber-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                
                {/* Emergency SOS Icon */}
                <a
                  href={ELCHME_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500/20 via-red-500/15 to-pink-500/20 border border-rose-400/30 hover:border-rose-400/60 hover:from-rose-500/30 hover:via-red-500/25 hover:to-pink-500/30 transition-all duration-300 flex items-center justify-center shadow-lg shadow-rose-500/10 hover:shadow-rose-500/20"
                  title="Emergency Empathy Alchemy - elchme.com"
                  data-testid="emergency-sos-icon"
                >
                  <EmergencySOSIcon size={20} className="text-rose-300 group-hover:text-rose-200 transition-colors" />
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-rose-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              </div>

              {/* Auto Generate Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAutoGenerate(true)}
                className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 hidden md:flex"
              >
                <GoldenSpiralIcon size={18} className="mr-2 text-purple-400" />
                Auto Generate
              </Button>

              {/* Community Charter Link */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 hidden md:flex"
              >
                <MandalaIcon size={18} className="mr-2 text-emerald-400" />
                Community Charter
              </Button>

              {/* Generate Dropdown */}
              <div className="relative">
                <Button
                  size="sm"
                  onClick={() => setShowOutputOptions(!showOutputOptions)}
                  disabled={isGenerating || Object.keys(responses).filter(k => responses[k]?.trim()).length === 0}
                  className={`border transition-all ${
                    Object.keys(responses).filter(k => responses[k]?.trim()).length > 0 
                      ? 'text-white border-transparent bg-indigo-600 hover:bg-indigo-700' 
                      : 'text-amber-300 border-amber-500/40 bg-amber-500/10'
                  }`}
                >
                  {isGenerating ? <RefreshCw size={14} className="animate-spin mr-2" /> : <FileText size={14} className="mr-2" />}
                  Generate
                  <ChevronRight size={14} className={`ml-1 transition-transform ${showOutputOptions ? 'rotate-90' : ''}`} />
                </Button>
                
                {/* Output Options Dropdown */}
                <AnimatePresence>
                  {showOutputOptions && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 top-full mt-2 w-72 bg-slate-800 rounded-xl border border-white/10 shadow-2xl overflow-hidden z-50"
                    >
                      <div className="p-2">
                        <p className="text-xs text-white/50 px-3 py-2">Choose output format:</p>
                        {OUTPUT_OPTIONS.map((option) => (
                          <button
                            key={option.key}
                            onClick={() => generateOutput(option.key)}
                            className="w-full p-3 rounded-lg text-left hover:bg-white/10 transition-colors flex items-start gap-3"
                          >
                            <option.icon size={18} className="text-indigo-400 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-white">{option.title}</p>
                              <p className="text-xs text-white/50">{option.description}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setToolsPanelCollapsed(!toolsPanelCollapsed)}
                className="hidden lg:flex text-indigo-300 hover:text-indigo-200 hover:bg-indigo-500/20 border border-indigo-500/30"
                title={toolsPanelCollapsed ? "Show Tools Panel" : "Hide Tools Panel"}
              >
                {toolsPanelCollapsed ? <PanelRightOpen size={18} /> : <PanelRightClose size={18} />}
              </Button>
              
              {/* Mobile nav toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMobileNav(!showMobileNav)}
                className="lg:hidden text-white"
              >
                {showMobileNav ? <X size={20} /> : <Menu size={20} />}
              </Button>
            </div>
          </div>
        </div>
        
        {/* Sign In Banner */}
        {!isAuthenticated && showSignInBanner && (
          <SignInBanner onClose={() => setShowSignInBanner(false)} />
        )}
      </header>

      {/* Mobile View Mode Toggle */}
      <div className="lg:hidden sticky top-[60px] z-30 bg-slate-900/95 backdrop-blur-md border-b border-white/10 px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant={mobileViewMode === 'vertical' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setMobileViewMode('vertical')}
              className={mobileViewMode === 'vertical' 
                ? 'bg-indigo-600 text-white text-xs' 
                : 'text-white/60 text-xs'}
            >
              <SacredListIcon size={14} className="mr-1" />
              All Sections
            </Button>
            <Button
              variant={mobileViewMode === 'focused' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setMobileViewMode('focused')}
              className={mobileViewMode === 'focused' 
                ? 'bg-indigo-600 text-white text-xs' 
                : 'text-white/60 text-xs'}
            >
              <SacredFocusIcon size={14} className="mr-1" />
              Focus Mode
            </Button>
          </div>
          <Badge className="bg-indigo-500/20 text-indigo-300 text-[10px]">
            {overallProgress}% Complete
          </Badge>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        <div className="container mx-auto px-4 py-6">
          
          {/* Mobile Vertical Scroll View */}
          {mobileViewMode === 'vertical' && (
            <div className="lg:hidden space-y-6 pb-24">
              {STARTUP_SECTIONS.map((section, sectionIdx) => (
                <div key={section.key} className="space-y-4">
                  {/* Section Header */}
                  <Card className="bg-white/5 backdrop-blur border-white/10 overflow-hidden">
                    <div className="h-1.5" style={{ backgroundColor: section.color }} />
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0"
                          style={{ backgroundColor: section.color + '30' }}
                        >
                          <section.icon className="w-6 h-6" style={{ color: section.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <Badge className="bg-white/10 text-white/70 text-[10px]">{section.compassPoint}</Badge>
                            <span className="text-xs text-emerald-400">
                              {section.subElements.filter(se => responses[`${section.key}_${se.key}`]?.trim()).length}/{section.subElements.length}
                            </span>
                          </div>
                          <h3 className="text-lg font-bold text-white truncate">{section.title}</h3>
                          <p className="text-xs text-white/50 truncate">{section.subtitle}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Sub-Elements for this section */}
                  <div className="space-y-3 pl-2">
                    {section.subElements.map((sub) => {
                      const key = `${section.key}_${sub.key}`;
                      const hasContent = responses[key]?.trim();
                      const isNPD = sub.title.includes('🆕');
                      
                      return (
                        <Card 
                          key={sub.key} 
                          className={`backdrop-blur border-white/10 ${
                            isNPD 
                              ? 'bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/20' 
                              : 'bg-white/5'
                          }`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start gap-2 mb-2">
                              {hasContent ? (
                                <SacredCheckIcon size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                              ) : (
                                <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex-shrink-0`}
                                  style={{ borderColor: isNPD ? '#f59e0b80' : section.color + '50' }} 
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className={`text-sm font-medium ${
                                  isNPD ? 'text-amber-300' : 'text-white'
                                }`}>
                                  {sub.title}
                                </h4>
                                <p className={`text-xs mt-0.5 ${
                                  isNPD ? 'text-amber-200/60' : 'text-white/50'
                                }`}>
                                  {sub.prompt}
                                </p>
                              </div>
                            </div>
                            <Textarea
                              value={responses[key] || ''}
                              onChange={(e) => {
                                const newResponses = { ...responses, [key]: e.target.value };
                                setResponses(newResponses);
                                localStorage.setItem('startup_studio_responses', JSON.stringify(newResponses));
                              }}
                              placeholder="Write your response..."
                              className={`min-h-[100px] text-sm border-white/10 text-white placeholder:text-white/30 focus:border-indigo-500/50 ${
                                isNPD ? 'bg-amber-500/10' : 'bg-black/30'
                              }`}
                            />
                            {/* Guidance tip */}
                            <div className="mt-2 p-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                              <p className="text-[10px] text-indigo-300 leading-relaxed">
                                💡 {sub.guidance}
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                  
                  {/* Section Divider */}
                  {sectionIdx < STARTUP_SECTIONS.length - 1 && (
                    <div className="flex items-center gap-4 py-4">
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                      <GoldenSpiralIcon size={20} className="text-indigo-400/50" />
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                    </div>
                  )}
                </div>
              ))}
              
              {/* Generate Button at bottom for mobile */}
              <div className="fixed bottom-4 left-4 right-4 z-40">
                <Button
                  onClick={() => setShowOutputOptions(true)}
                  disabled={isGenerating || overallProgress < 10}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white shadow-xl shadow-indigo-500/30"
                >
                  {isGenerating ? <RefreshCw size={16} className="animate-spin mr-2" /> : <FileText size={16} className="mr-2" />}
                  Generate Business Plan ({overallProgress}% Complete)
                </Button>
              </div>
            </div>
          )}

          {/* Desktop & Mobile Focused View */}
          <div className={`${mobileViewMode === 'vertical' ? 'hidden lg:grid' : 'grid'} gap-6 ${toolsPanelCollapsed ? 'lg:grid-cols-[280px_1fr]' : 'lg:grid-cols-[280px_1fr_320px]'}`}>
            
            {/* Left Sidebar - Navigation */}
            <aside className="hidden lg:block space-y-6">
              {/* Compass Rose */}
              <Card className="bg-white/5 backdrop-blur border-white/10 p-4">
                <div className="flex justify-center mb-4">
                  <BusinessCompassRose
                    sections={STARTUP_SECTIONS}
                    activeSection={activeSection}
                    onSectionClick={handleSectionClick}
                    size={200}
                    responses={responses}
                  />
                </div>
              </Card>
              
              {/* Section List */}
              <Card className="bg-white/5 backdrop-blur border-white/10">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-white/70">Compass Points</CardTitle>
                </CardHeader>
                <CardContent className="p-3">
                  <CompassNavSidebar
                    sections={STARTUP_SECTIONS}
                    activeSection={activeSection}
                    onSectionClick={handleSectionClick}
                    responses={responses}
                  />
                </CardContent>
              </Card>
            </aside>
            
            {/* Center panel - Editor */}
            <div className="space-y-4">
              {currentSubElement && (
                <motion.div key={`${activeSection}-${activeSubElement}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <Card className="bg-white/5 backdrop-blur border-white/10">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2 text-white">
                          <SacredFounderIcon size={20} style={{ color: currentSection.color }} />
                          {currentSubElement.title}
                        </CardTitle>
                        <Badge style={{ backgroundColor: currentSection.color + '30', color: currentSection.color }}>
                          {currentSection.compassPoint}
                        </Badge>
                      </div>
                      <CardDescription className="text-white/60">{currentSubElement.prompt}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        value={getCurrentContent()}
                        onChange={(e) => handleContentChange(e.target.value)}
                        placeholder="Write your response here..."
                        className="min-h-[250px] text-sm bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-indigo-500"
                      />
                      
                      {/* Guidance */}
                      <div className="mt-3 p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                        <p className="text-xs text-indigo-300 flex items-start gap-2">
                          <AncientSunIcon size={16} className="mt-0.5 flex-shrink-0 text-indigo-400" />
                          <span>{currentSubElement.guidance}</span>
                        </p>
                      </div>

                      {/* Action Buttons - Cross-reactivity */}
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        {/* Beautiful Add to Tasks Button */}
                        <motion.button
                          onClick={() => {
                            handleAddTask({
                              title: `Complete: ${currentSubElement.title}`,
                              category: currentSection.key,
                              priority: 'medium',
                              source: 'compass'
                            });
                            toast.success('Added to your tasks!');
                          }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="relative overflow-hidden px-4 py-2 rounded-xl border border-white/20 hover:border-indigo-400/50 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-amber-500/10 transition-all group"
                          data-testid="add-to-tasks-btn"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-amber-500/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                          <div className="relative flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                              <Plus size={12} className="text-white" />
                            </div>
                            <span className="text-sm font-medium text-white/80 group-hover:text-white">Add to Tasks</span>
                          </div>
                        </motion.button>
                        
                        {/* Brand Checker - show on brand-related sections */}
                        {(currentSubElement.key === 'brand_name' || currentSubElement.key === 'ip_due_diligence' || currentSubElement.key === 'visual_identity') && (
                          <motion.button
                            onClick={() => setShowBrandChecker(true)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="relative overflow-hidden px-4 py-2 rounded-xl border border-indigo-500/30 hover:border-indigo-400/50 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 transition-all group"
                            data-testid="open-brand-checker-btn"
                          >
                            <div className="relative flex items-center gap-2">
                              <MetatronIcon size={16} className="text-indigo-400 group-hover:text-indigo-300" />
                              <span className="text-sm font-medium text-indigo-300 group-hover:text-indigo-200">Open Brand Checker</span>
                            </div>
                          </motion.button>
                        )}
                        
                        {/* Belonging Sessions link - show on team sections */}
                        {(currentSubElement.key === 'team_belonging' || currentSubElement.key === 'founding_team' || currentSubElement.key === 'trust_building') && (
                          <motion.a
                            href="https://belongingsessions.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="relative overflow-hidden px-4 py-2 rounded-xl border border-emerald-500/30 hover:border-emerald-400/50 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 transition-all group flex items-center gap-2"
                          >
                            <MandalaIcon size={16} className="text-emerald-400 group-hover:text-emerald-300" />
                            <span className="text-sm font-medium text-emerald-300 group-hover:text-emerald-200">Explore Belonging Sessions</span>
                            <ExternalLink size={12} className="text-emerald-400/60" />
                          </motion.a>
                        )}
                      </div>

                      {/* Addressed Elsewhere Button */}
                      {currentSection.addressedIn && currentSection.addressedIn.length > 0 && (
                        <div className="mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                          <p className="text-xs text-emerald-300 flex items-center gap-2">
                            <ExternalLink size={14} />
                            <span>Related in Community Charter: {currentSection.addressedIn.join(', ')}</span>
                          </p>
                        </div>
                      )}

                      {/* Navigation */}
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const subIndex = currentSection.subElements.findIndex(s => s.key === activeSubElement);
                            if (subIndex > 0) {
                              setActiveSubElement(currentSection.subElements[subIndex - 1].key);
                            }
                          }}
                          disabled={currentSection.subElements.findIndex(s => s.key === activeSubElement) === 0}
                          className="text-white/70"
                        >
                          <ChevronLeft size={16} className="mr-1" /> Previous
                        </Button>
                        
                        <span className="text-xs text-white/40">
                          {currentSection.subElements.findIndex(s => s.key === activeSubElement) + 1} of {currentSection.subElements.length}
                        </span>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const subIndex = currentSection.subElements.findIndex(s => s.key === activeSubElement);
                            if (subIndex < currentSection.subElements.length - 1) {
                              setActiveSubElement(currentSection.subElements[subIndex + 1].key);
                            }
                          }}
                          disabled={currentSection.subElements.findIndex(s => s.key === activeSubElement) === currentSection.subElements.length - 1}
                          className="text-white/70"
                        >
                          Next <ChevronRight size={16} className="ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Sub-elements navigation */}
                  <Card className="bg-white/5 backdrop-blur border-white/10 mt-4">
                    <CardContent className="p-4">
                      <div className="flex flex-wrap gap-2">
                        {currentSection.subElements.map((sub) => {
                          const hasContent = responses[`${activeSection}_${sub.key}`]?.trim();
                          const isActive = sub.key === activeSubElement;
                          return (
                            <button
                              key={sub.key}
                              onClick={() => setActiveSubElement(sub.key)}
                              className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 transition-all ${
                                isActive 
                                  ? 'bg-white/20 text-white' 
                                  : hasContent 
                                    ? 'bg-white/10 text-white/80' 
                                    : 'bg-white/5 text-white/50 hover:bg-white/10'
                              }`}
                            >
                              {hasContent ? <Check size={12} className="text-green-400" /> : <Circle size={12} />}
                              {sub.title}
                            </button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Further Reading */}
                  {currentSection.furtherReading && currentSection.furtherReading.length > 0 && (
                    <Card className="bg-white/5 backdrop-blur border-white/10 mt-4">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-white/70 flex items-center gap-2">
                          <BookOpen size={16} />
                          Want to Go Deeper?
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-2">
                          {currentSection.furtherReading.map((book, i) => (
                            <div key={i} className="flex items-start gap-2 text-xs">
                              <ArrowUpRight size={12} className="text-indigo-400 mt-0.5 flex-shrink-0" />
                              <div>
                                <span className="text-white/80">{book.title}</span>
                                <span className="text-white/40 ml-1">— {book.note}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </motion.div>
              )}
            </div>
            
            {/* Right sidebar - Tools Panel */}
            {!toolsPanelCollapsed && (
              <ToolsPanel
                projectId="startup"
                sectionKey={activeSection}
                subElementKey={activeSubElement}
                context={getCurrentContent()}
                onInsert={handleInsertFromAI}
                isCollapsed={toolsPanelCollapsed}
                onToggleCollapse={() => setToolsPanelCollapsed(!toolsPanelCollapsed)}
                reminders={reminders}
                onAddReminder={(r) => setReminders([...reminders, r])}
                onToggleReminder={(id) => setReminders(reminders.map(r => r.id === id ? { ...r, completed: !r.completed } : r))}
                onDeleteReminder={(id) => setReminders(reminders.filter(r => r.id !== id))}
                showStartupLink={false}
                theme="dark"
                studioType="startup"
              />
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation Sheet - Compass Points */}
      <AnimatePresence>
        {showMobileNav && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] lg:hidden"
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowMobileNav(false)} />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="absolute left-0 top-0 bottom-0 w-[85%] max-w-sm bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-r border-white/20 overflow-auto shadow-2xl"
            >
              <div className="sticky top-0 bg-slate-900/95 backdrop-blur-md border-b border-white/10 p-4 flex items-center justify-between">
                <h3 className="font-heading text-lg text-white">Compass Points</h3>
                <button 
                  onClick={() => setShowMobileNav(false)}
                  className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-4">
                <CompassNavSidebar
                  sections={STARTUP_SECTIONS}
                  activeSection={activeSection}
                  onSectionClick={handleSectionClick}
                  responses={responses}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Tools Button */}
      <AnimatePresence>
        {!showToolsPanel && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            className="fixed bottom-20 right-4 z-40 lg:hidden w-14 h-14 rounded-full shadow-lg flex items-center justify-center"
            style={{ background: STARTUP_THEME.primary }}
            onClick={() => setShowToolsPanel(true)}
          >
            <MetatronIcon size={26} className="text-white" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Mobile Tools Panel */}
      <AnimatePresence>
        {showToolsPanel && (
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            className="fixed inset-x-0 bottom-0 z-50 lg:hidden bg-slate-900 border-t border-white/10 rounded-t-2xl shadow-2xl"
            style={{ maxHeight: '85vh' }}
          >
            <div className="p-4 border-b border-white/10">
              <div className="flex justify-between items-center">
                <h3 className="font-heading text-lg text-white">Tools</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowToolsPanel(false)} className="text-white">
                  <X size={20} />
                </Button>
              </div>
            </div>
            <div className="overflow-auto" style={{ maxHeight: 'calc(85vh - 60px)' }}>
              <ToolsPanel
                projectId="startup"
                sectionKey={activeSection}
                subElementKey={activeSubElement}
                context={getCurrentContent()}
                onInsert={(text) => { handleInsertFromAI(text); setShowToolsPanel(false); }}
                reminders={reminders}
                onAddReminder={(r) => setReminders([...reminders, r])}
                onToggleReminder={(id) => setReminders(reminders.map(r => r.id === id ? { ...r, completed: !r.completed } : r))}
                onDeleteReminder={(id) => setReminders(reminders.filter(r => r.id !== id))}
                showStartupLink={false}
                theme="dark"
                studioType="startup"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Generated Plan Modal */}
      <AnimatePresence>
        {showPlanModal && generatedPlan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowPlanModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl bg-slate-900 border border-white/10 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10 bg-slate-800/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center">
                    <FileText size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">
                      {OUTPUT_OPTIONS.find(o => o.key === selectedOutput)?.title || 'Your Document'}
                    </h2>
                    <p className="text-xs text-white/50">
                      Generated {new Date(generatedPlan.generated_at).toLocaleString()} • {generatedPlan.word_count} words
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Share Button */}
                  <Button
                    size="sm"
                    onClick={handleShareDocument}
                    disabled={isSharingDocument}
                    className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white border-0"
                    data-testid="share-document-btn"
                  >
                    {isSharingDocument ? (
                      <RefreshCw size={16} className="mr-2 animate-spin" />
                    ) : (
                      <Share2 size={16} className="mr-2" />
                    )}
                    Share
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      navigator.clipboard.writeText(generatedPlan.content);
                      toast.success('Copied to clipboard!');
                    }}
                    className="text-white/70 hover:text-white hover:bg-white/10"
                  >
                    <Copy size={16} className="mr-2" />
                    Copy
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      const blob = new Blob([generatedPlan.content], { type: 'text/markdown' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${selectedOutput}.md`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success('Downloaded!');
                    }}
                    className="text-white/70 hover:text-white hover:bg-white/10"
                  >
                    <Download size={16} className="mr-2" />
                    Download
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowPlanModal(false);
                      setDocumentShareUrl(null);
                    }}
                    className="text-white/70 hover:text-white hover:bg-white/10"
                  >
                    <X size={20} />
                  </Button>
                </div>
              </div>
              
              {/* Share URL Banner */}
              {documentShareUrl && (
                <div className="px-4 py-3 bg-gradient-to-r from-emerald-500/20 to-emerald-600/10 border-b border-emerald-500/30">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                        <Check size={16} className="text-emerald-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-emerald-300">Share link created!</p>
                        <p className="text-xs text-emerald-400/60 truncate">{documentShareUrl}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        navigator.clipboard.writeText(documentShareUrl);
                        toast.success('Link copied!');
                      }}
                      className="text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/20 flex-shrink-0"
                    >
                      <Copy size={14} className="mr-1" />
                      Copy Link
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Modal Content */}
              <div className="p-6 overflow-auto max-h-[calc(90vh-80px)]">
                <div className="prose prose-invert prose-sm max-w-none">
                  <div className="whitespace-pre-wrap text-white/80 leading-relaxed font-mono text-sm">
                    {generatedPlan.content}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pro Forma Modal */}
      <AnimatePresence>
        <ProFormaModal 
          isOpen={showProFormaModal} 
          onClose={() => setShowProFormaModal(false)}
          responses={responses}
        />
      </AnimatePresence>

      {/* Auto Generate Modal */}
      <AnimatePresence>
        {showAutoGenerate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
            onClick={() => setShowAutoGenerate(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="w-full max-w-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl border border-white/20 shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-white/10 bg-gradient-to-r from-purple-600/20 via-indigo-600/20 to-amber-600/20">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                    <GoldenSpiralIcon size={28} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Auto Generate</h2>
                    <p className="text-sm text-white/60">AI-powered business plan generation</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Describe your startup in a few sentences
                  </label>
                  <textarea
                    value={autoGenDescription}
                    onChange={(e) => setAutoGenDescription(e.target.value)}
                    placeholder="e.g., A SaaS platform that helps small businesses automate their customer support using AI chatbots. We charge $99/month per seat and target companies with 10-100 employees..."
                    className="w-full h-32 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none resize-none"
                    data-testid="auto-generate-description"
                  />
                  <p className="text-xs text-white/40 mt-2">
                    Include: what you do, who you serve, how you make money, what makes you different
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <p className="text-sm text-white/70 mb-3">Choose what to generate:</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={() => handleAutoGenerate('sections')}
                      disabled={isAutoGenerating || !autoGenDescription.trim()}
                      className="flex-col h-auto py-4 bg-gradient-to-br from-indigo-500/20 to-indigo-600/10 hover:from-indigo-500/30 hover:to-indigo-600/20 border border-indigo-500/30"
                      data-testid="auto-generate-sections-btn"
                    >
                      {isAutoGenerating ? (
                        <RefreshCw size={20} className="mb-2 animate-spin" />
                      ) : (
                        <LegendaryCompass size={24} className="mb-2 text-indigo-400" />
                      )}
                      <span className="text-sm font-medium">Fill All Sections</span>
                      <span className="text-xs text-white/50 mt-1">Complete 8-point compass</span>
                    </Button>
                    <Button
                      onClick={() => handleAutoGenerate('document')}
                      disabled={isAutoGenerating || !autoGenDescription.trim()}
                      className="flex-col h-auto py-4 bg-gradient-to-br from-purple-500/20 to-purple-600/10 hover:from-purple-500/30 hover:to-purple-600/20 border border-purple-500/30"
                      data-testid="auto-generate-document-btn"
                    >
                      {isAutoGenerating ? (
                        <RefreshCw size={20} className="mb-2 animate-spin" />
                      ) : (
                        <LotusIcon size={24} className="mb-2 text-purple-400" />
                      )}
                      <span className="text-sm font-medium">Generate One-Pager</span>
                      <span className="text-xs text-white/50 mt-1">Instant business summary</span>
                    </Button>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-amber-300/70 text-xs">
                  <AncientSunIcon size={16} className="text-amber-400" />
                  <span>Generated content is a starting point. Review and customize for your specific situation.</span>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-white/10 flex justify-end">
                <Button
                  variant="ghost"
                  onClick={() => setShowAutoGenerate(false)}
                  className="text-white/60 hover:text-white"
                >
                  Cancel
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Brand Checker Modal */}
      <AnimatePresence>
        {showBrandChecker && (
          <BrandChecker
            onClose={() => setShowBrandChecker(false)}
            onAddToTasks={handleAddTask}
            startupResponses={responses}
          />
        )}
      </AnimatePresence>

      {/* Ecosystem Navigation */}
      <EcosystemNav currentSite="community-forge" />
    </div>
  );
};

export default StartupStudio;
